import * as React from 'react';
import { useCallback } from 'react';
import type { Semester } from '~/Features/Semester.csharp';
import { Content } from '~/Pages/SubjectInfoPage/Components/Content/Content';
import { SubjectInfo } from '~/Pages/SubjectInfoPage/Components/SubjectInfo/SubjectInfo';
import type { SubjectInfoDescriptionViewModel } from '~/Pages/SubjectInfoPage/SubjectInfoDescriptionViewModel.csharp';
import type { SemesterDropdownItem } from '~/Pages/SubjectInfoPage/ViewModel/SemesterDropdownItem.csharp';
import type { SubjectContentViewModel } from '~/Pages/SubjectInfoPage/ViewModel/SubjectContentViewModel.csharp';
import baseStyles from '~/Pages/SyllabusListPage/SyllabusInfoPageBase.module.css';
import { useFetchData } from '~/Partials/FetchData/useFetchData';
import { changeQueryParametersInActiveUrl, formatQueryParameters } from '~/Partials/SearchBase/QueryParameterUtils';

export const SubjectInfoContent: React.FC<React.PropsWithChildren<SubjectContentViewModel>> = props => {
    const {
        name,
        code,
        version,
        description,
        getDescriptionUrl,
        onlineSubjectPageUrl,
        placeholder,
        semesterDropdownItems,
        englishName,
        points,
        studyLevel,
        translations,
    } = props;
    const [currentSemester, setCurrentSemester] = React.useState<SemesterDropdownItem>();

    React.useEffect(() => {
        const initSemester = semesterDropdownItems.find(
            item => item.semester.year === description.year && item.semester.period === description.period
        );

        if (initSemester) {
            setCurrentSemester(initSemester);
        }
    }, []);

    const getQueryParams = (semester: SemesterDropdownItem) => [
        { name: 'year', value: semester.semester.year.toString() },
        { name: 'period', value: semester.semester.period.toString() },
    ];

    const setQueryParams = useCallback((semester: SemesterDropdownItem) => {
        const queryParameters = getQueryParams(semester);
        changeQueryParametersInActiveUrl(queryParameters);
    }, []);

    const descriptionUrl = currentSemester
        ? `${getDescriptionUrl}${formatQueryParameters(getQueryParams(currentSemester))}`
        : '';

    const { loading, error, data } = useFetchData<SubjectInfoDescriptionViewModel, Semester>({
        url: descriptionUrl,
        shouldMakeRequest: !!descriptionUrl,
    });

    const subjectName = data?.name ?? name;

    return (
        <section className={baseStyles.Content}>
            <div className={baseStyles.Heading}>
                <h1 className={baseStyles.Code}>
                    {translations.subjectDescription} {code}
                </h1>
                <h2 className={baseStyles.Name}>{subjectName}</h2>
            </div>
            {currentSemester && description ? (
                <SubjectInfo
                    selectedSemester={currentSemester}
                    subjectDescription={description}
                    subjectData={data}
                    isLoading={loading}
                    fetchingError={error}
                    placeholder={placeholder}
                    version={version}
                    semesterDropdownItems={semesterDropdownItems}
                    studyLevel={studyLevel}
                    points={points}
                    englishName={englishName}
                    onlineSubjectPageUrl={onlineSubjectPageUrl}
                    translations={translations}
                    code={code}
                    setCurrentSemester={setCurrentSemester}
                    setQueryParams={setQueryParams}
                />
            ) : (
                <Content
                    code={code}
                    translations={translations}
                    onlineSubjectPageUrl={onlineSubjectPageUrl}
                    currentSemester={currentSemester}
                    semesterDropdownItems={semesterDropdownItems}
                    points={points}
                    version={version}
                    placeholder={placeholder}
                    studyLevel={studyLevel}
                    englishName={englishName}
                    setCurrentSemester={setCurrentSemester}
                    setQueryParams={setQueryParams}
                />
            )}
        </section>
    );
};
